import { getSpeakerKey, isDisplayableDiarization } from '@app/modules/diarization/speaker-utils';
import { DiarizationFormattedType } from '@app/modules/search/types';
import { Timestamp, toTimestamp } from '@mi-front/core';
import { VOICE_LANE_PREFIX } from './build-person-lanes';
import { FaceRecognitionRangeItem, PersonAppearance } from './types';

/** `1722366371000__190611.ts` → 1722366371000. */
export function getSegmentTsFromFilename(filename: string): number | null {
  const prefix = filename.split('__')[0];
  const ts = Number(prefix);
  return filename.includes('__') && Number.isFinite(ts) && ts > 0 ? ts : null;
}

/**
 * Normaliza documentos PNT facerecognition a apariciones absolutas.
 * Requiere `filename` en cada ítem para anclar start/end al epoch del segmento.
 */
export function mapFaceRecognitionsToAppearances(
  items: Array<Pick<FaceRecognitionRangeItem, 'filename' | 'segments'>>,
): PersonAppearance[] {
  const result: PersonAppearance[] = [];
  for (const item of items) {
    const segmentTs = getSegmentTsFromFilename(item.filename ?? '');
    if (segmentTs === null) {
      continue;
    }
    for (const s of item.segments ?? []) {
      if (!s.person_id) {
        continue;
      }
      result.push({
        laneKey: s.person_id,
        personId: s.person_id,
        source: 'face',
        from: (segmentTs + s.start * 1000) as Timestamp,
        to: (segmentTs + s.end * 1000) as Timestamp,
        score: s.confidence,
        isUnknown: s.is_unknown,
        fallbackLabel: '',
        crop: s.is_unknown ? s.face_display_crop : undefined,
      });
    }
  }
  return result;
}

/**
 * Misma convención de tiempo que los marcadores de diarización anteriores: `time` + start*1000.
 * (`time` ya trae sumado `segment.start` en ms por useDiarization; el error es < 1 s.)
 */
export function mapDiarizationToAppearances(
  diarization: NonNullable<DiarizationFormattedType>[],
): PersonAppearance[] {
  const result: PersonAppearance[] = [];
  for (const d of diarization) {
    if (!isDisplayableDiarization(d)) {
      continue;
    }
    const base = toTimestamp(d.time);
    const key = getSpeakerKey(d);
    result.push({
      laneKey: d.person_id ?? `${VOICE_LANE_PREFIX}${key}`,
      personId: d.person_id ?? null,
      source: 'voice',
      from: (base + d.segment.start * 1000) as Timestamp,
      to: (base + d.segment.end * 1000) as Timestamp,
      score: d.distance,
      isUnknown: false,
      fallbackLabel: d.voice_label || d.label || '',
    });
  }
  return result;
}
